import moment from 'moment';

import { UserSitesAi200ItemsItem } from '@newfold/huapi-js/src/index.schemas';

import { AI_PRODUCT_SITES_PAGE_SIZE } from '~/apps/AiProductPages/utils/constants';

const BILLING_EXPIRATION_FORMAT = 'YYYY.MMMM.DD HH:mm:ss';
const DATE_ADDED_FORMAT = 'YYYY-MM-DD HH:mm:ss';

/** Builds trial windows relative to today so banner progress stays accurate in mock mode. */
const createFreeTrialProgressScenario = (
  daysRemaining: number,
  totalTrialDays: number,
) => {
  const expirationMoment = moment().startOf('day').add(daysRemaining, 'days');
  const dateAddedMoment = expirationMoment
    .clone()
    .subtract(totalTrialDays, 'days')
    .hour(10)
    .minute(0)
    .second(0);

  return {
    dateAdded: dateAddedMoment.format(DATE_ADDED_FORMAT),
    expirationDate: expirationMoment.format(BILLING_EXPIRATION_FORMAT),
  };
};

const FREE_TRIAL_PROGRESS_SCENARIOS = [
  createFreeTrialProgressScenario(18, 20),
  createFreeTrialProgressScenario(15, 20),
  createFreeTrialProgressScenario(10, 20),
  createFreeTrialProgressScenario(5, 20),
  createFreeTrialProgressScenario(2, 14),
  createFreeTrialProgressScenario(1, 14),
];

const getPaidPlanExpirationDate = () =>
  moment().startOf('day').add(1, 'year').format(BILLING_EXPIRATION_FORMAT);

const getFreeTrialDates = (scenarioIndex: number) =>
  FREE_TRIAL_PROGRESS_SCENARIOS[
    scenarioIndex % FREE_TRIAL_PROGRESS_SCENARIOS.length
  ];

const GET_ONLINE_NAMES = [
  'Sunrise Bakery',
  'Urban Yoga Studio',
  'Lakeview Photography',
  'Green Thumb Nursery',
  'Coastal Crafts',
  'Metro Fitness Hub',
  'Willow & Wren',
  'Peak Performance Coaching',
  'Harbor Home Decor',
  'Bright Minds Tutoring',
  'Silverline Consulting',
  'North Star Marketing',
];

const SELL_ONLINE_NAMES = [
  'Artisan Soap Works',
  'Pet Pantry Plus',
  'Vintage Vinyl Shop',
  'Fresh Bloom Florals',
  'Trailhead Gear',
  'Sweet Spice Market',
  'Modern Minimal Home',
  'Kids Corner Toys',
  'Craft Coffee Roasters',
  'Wellness Essentials',
  'Outdoor Adventure Co.',
  'Handmade Jewelry Box',
  'Organic Skincare Lab',
];

type MockAiSiteVariant = 'get-online' | 'sell-online';

const createMockAiSite = (
  id: number,
  name: string,
  variant: MockAiSiteVariant,
  options?: {
    isPaid?: boolean;
    hasWooCommerce?: boolean;
    freeTrialScenarioIndex?: number;
  },
): UserSitesAi200ItemsItem => {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const isPaid = options?.isPaid ?? id % 3 === 0;
  const isGetOnline = variant === 'get-online';
  const freeTrialDates = getFreeTrialDates(
    options?.freeTrialScenarioIndex ??
      id % FREE_TRIAL_PROGRESS_SCENARIOS.length,
  );

  return {
    id,
    account_back_ref: `WN.HP.73211${id}`,
    account_id: String(1832000 + id),
    account_status: 'active',
    name,
    status: 'active',
    type: 'wordpress',
    detected_type: 'wordpress',
    docroot: 'public_html',
    docpath: 'public_html',
    url: `https://${slug}.bluehost.com`,
    pending_url: isPaid ? null : `https://${slug}.com`,
    url_is_provisional: !isPaid,
    install_blu_ai: isGetOnline ? '1' : null,
    install_blu_ai_store: isGetOnline ? null : isPaid ? 'true' : 'premium',
    has_woocommerce: options?.hasWooCommerce ?? (isGetOnline && isPaid),
    tenweb_upgrade_date: isPaid ? '2026-08-20 10:00:00' : null,
    date_added: isPaid
      ? `2026-08-${String((id % 28) + 1).padStart(2, '0')} 10:00:00`
      : freeTrialDates.dateAdded,
    billing: isGetOnline
      ? {
          prod_name: 'Get Online',
          prod_code: isPaid ? 'AI_SITE_WEBSITE' : 'BH_PKG_FREE_TRIAL',
          expiration_date: isPaid
            ? getPaidPlanExpirationDate()
            : freeTrialDates.expirationDate,
        }
      : {
          prod_name: 'Sell Online',
          prod_code: isPaid ? 'AI_SITE_COMMERCE' : 'BH_PKG_FREE_TRIAL_COMMERCE',
          expiration_date: isPaid
            ? getPaidPlanExpirationDate()
            : freeTrialDates.expirationDate,
        },
  };
};

const generatedGetOnlineSites = GET_ONLINE_NAMES.map((name, index) =>
  createMockAiSite(495290 + index, name, 'get-online', {
    isPaid: index >= 6 ? index % 2 === 0 : false,
    hasWooCommerce: index % 2 === 0,
    freeTrialScenarioIndex: index < 6 ? index : undefined,
  }),
);

const generatedSellOnlineSites = SELL_ONLINE_NAMES.map((name, index) =>
  createMockAiSite(495310 + index, name, 'sell-online', {
    isPaid: index >= 4 ? index % 2 === 1 : false,
    freeTrialScenarioIndex: index < 4 ? index + 1 : undefined,
  }),
);

export const mockGetOnlineSites: UserSitesAi200ItemsItem[] = [
  {
    id: 495281,
    account_back_ref: 'WN.HP.732115496',
    account_id: '1831113',
    account_status: 'active',
    name: 'The Parlor',
    status: 'active',
    type: 'wordpress',
    detected_type: 'wordpress',
    docroot: 'public_html',
    docpath: 'public_html',
    url: 'https://temp-preview-bf83k.bluehost.com',
    pending_url: 'https://theparlor.com',
    url_is_provisional: true,
    install_blu_ai: '1',
    install_blu_ai_store: null,
    has_woocommerce: true,
    tenweb_upgrade_date: null,
    date_added: getFreeTrialDates(0).dateAdded,
    billing: {
      prod_name: 'Get Online',
      prod_code: 'BH_PKG_FREE_TRIAL',
      expiration_date: getFreeTrialDates(0).expirationDate,
    },
  },
  {
    id: 495282,
    account_back_ref: 'WN.HP.732115497',
    account_id: '1831114',
    account_status: 'active',
    name: 'Milestone Chef Treats',
    status: 'active',
    type: 'wordpress',
    detected_type: 'wordpress',
    docroot: 'public_html',
    docpath: 'public_html',
    url: 'https://hjd.bnl.mybluehost.me',
    pending_url: 'https://milestonecheftreats.com',
    url_is_provisional: true,
    install_blu_ai: '1',
    install_blu_ai_store: null,
    has_woocommerce: true,
    tenweb_upgrade_date: null,
    date_added: getFreeTrialDates(2).dateAdded,
    billing: {
      prod_name: 'Get Online',
      prod_code: 'BH_PKG_FREE_TRIAL',
      expiration_date: getFreeTrialDates(2).expirationDate,
    },
  },
  {
    id: 495283,
    account_back_ref: 'WN.HP.732115498',
    account_id: '1831115',
    account_status: 'active',
    name: 'The Parlor',
    status: 'active',
    type: 'wordpress',
    detected_type: 'wordpress',
    docroot: 'public_html',
    docpath: 'public_html',
    url: 'https://theparlor.com',
    pending_url: null,
    url_is_provisional: false,
    install_blu_ai: '1',
    install_blu_ai_store: null,
    has_woocommerce: false,
    tenweb_upgrade_date: '2026-08-20 10:00:00',
    date_added: '2026-08-01 10:00:00',
    billing: {
      prod_name: 'Get Online',
      prod_code: 'AI_SITE_WEBSITE',
      expiration_date: getPaidPlanExpirationDate(),
    },
  },
  ...generatedGetOnlineSites,
];

export const mockSellOnlineSites: UserSitesAi200ItemsItem[] = [
  {
    id: 495284,
    account_back_ref: 'WN.HP.732115499',
    account_id: '1831116',
    account_status: 'active',
    name: 'Cologne Candle Co.',
    status: 'active',
    type: 'wordpress',
    detected_type: 'wordpress',
    docroot: 'public_html',
    docpath: 'public_html',
    url: 'https://temp-preview-9dq2.bluehost.com',
    pending_url: 'https://colognecandleco.com',
    url_is_provisional: true,
    install_blu_ai: '1',
    install_blu_ai_store: 'premium',
    has_woocommerce: true,
    tenweb_upgrade_date: null,
    date_added: getFreeTrialDates(3).dateAdded,
    billing: {
      prod_name: 'Sell Online',
      prod_code: 'BH_PKG_FREE_TRIAL_COMMERCE',
      expiration_date: getFreeTrialDates(3).expirationDate,
    },
  },
  {
    id: 495285,
    account_back_ref: 'WN.HP.732115500',
    account_id: '1831117',
    account_status: 'active',
    name: 'Cologne Candle Co.',
    status: 'active',
    type: 'wordpress',
    detected_type: 'wordpress',
    docroot: 'public_html',
    docpath: 'public_html',
    url: 'https://colognecandleco.com',
    pending_url: null,
    url_is_provisional: false,
    install_blu_ai: '1',
    install_blu_ai_store: 'true',
    has_woocommerce: true,
    tenweb_upgrade_date: '2026-08-27 07:13:25',
    date_added: '2026-08-10 10:00:00',
    billing: {
      prod_name: 'Sell Online',
      prod_code: 'AI_SITE_COMMERCE',
      expiration_date: getPaidPlanExpirationDate(),
    },
  },
  ...generatedSellOnlineSites,
];

// Legacy single-item exports for backwards compatibility
export const mockGetOnlineSite = mockGetOnlineSites[0];
export const mockSellOnlineSite = mockSellOnlineSites[0];

const parseBooleanParam = (value: string | null) =>
  value === 'true' || value === '1';

const filterSitesBySearch = (
  sites: UserSitesAi200ItemsItem[],
  search: string,
) => {
  const normalizedSearch = search.trim().toLowerCase();
  if (!normalizedSearch) return sites;

  return sites.filter((site) => {
    const name = site.name?.toLowerCase() ?? '';
    const url = site.url?.toLowerCase() ?? '';
    const pendingUrl = site.pending_url?.toLowerCase() ?? '';

    return (
      name.includes(normalizedSearch) ||
      url.includes(normalizedSearch) ||
      pendingUrl.includes(normalizedSearch)
    );
  });
};

export const getAiProductSitesMockResponse = (requestUrl: string) => {
  const url = new URL(requestUrl);
  const includeAiSites = parseBooleanParam(url.searchParams.get('ai_sites'));
  const includeAiStores = parseBooleanParam(url.searchParams.get('ai_stores'));
  const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
  const limit = Math.max(
    1,
    Number(url.searchParams.get('limit') ?? AI_PRODUCT_SITES_PAGE_SIZE),
  );
  const search = url.searchParams.get('search') ?? '';

  let items: UserSitesAi200ItemsItem[] = [];

  if (includeAiSites) {
    items = [...items, ...mockGetOnlineSites];
  }

  if (includeAiStores) {
    items = [...items, ...mockSellOnlineSites];
  }

  if (!includeAiSites && !includeAiStores) {
    items = [...mockGetOnlineSites, ...mockSellOnlineSites];
  }

  items = filterSitesBySearch(items, search);

  const total = items.length;
  const pages = Math.max(1, Math.ceil(total / limit));
  const start = (page - 1) * limit;

  return {
    total,
    page,
    pages,
    limit,
    items: items.slice(start, start + limit),
  };
};
